/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import {declareEntity, BACKGROUND_ADMIN} from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';

export const ACCESS_TYPE_REGULARUSER = 'regularUser';
export const ACCESS_TYPE_DEVELOPER = 'developer';
export const ACCESS_TYPE_PARTNER1 = 'partner1';
export const ACCESS_TYPE_PARTNER2 = 'partner2';

export const NOTIFY_FREQ = {
    'daily': {
        name: 'Daily',
    },
    'weekly': {
        name: 'Weekly',
    },
    '6hours': {
        name: 'Every 6 hours',
    },
    '2hours': {
        name: 'Every 2 hours',
    },
    'hourly': {
        name: 'Hourly',
    },
};

export default declareEntity({
    entity: "users",
    graphQL: "User",
    name: "Users",
    dynamoPrefix: 'app',
    storageType: 'central',
    defaultOrder: ['surname', 'firstname'],
    defaultTitle: (data) => {
        return data['firstname'] + ' ' + data['surname'];
    },
	// eslint-disable-next-line max-params
    permissionFunc: (action, permissionObject, data, organisationId) => {
        if (permissionObject && permissionObject.id === BACKGROUND_ADMIN) {
            return true;
        }

        /* user can edit only his own profile */
        if (data && permissionObject && (data['id'] === permissionObject.id || data['realId'] === permissionObject.id)) {
            return true;
        }

        return false;
    },
    fields: (types) => { return {
        id: {
            name: "User ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
        },
        email: {
            name: "Email",
            type: "string",
            defaultValue: "",
            trim: true,
            filterable: true,
            userEditable: false,
            validations: {
                isEmail: true
            }
        },
        phoneNumber: {
            name: "Phone Number",
            type: "string",
            trim: true,
            defaultValue: "",
        },
        externalId: {
            name: "External ID",
            type: "string",
            defaultValue: "",
            filterable: true,
        },
        otherEmails: {
            name: "Other emails",
            type: "text",
            defaultValue: "",
			description: "Other emails to be filtered out from result separated by comma (,).",
        },
        name: {
            name: "Full name",
            type: "string",
            userEditable: false,
            controllerFn: 'getFullName',
            import: false,
            export: false,
        },
        firstname: {
            name: "First name",
            type: "string",
            defaultValue: "",
        },
        surname: {
            name: "Surname",
            type: "string",
            defaultValue: "",
        },
        nickname: {
            name: "Nickname",
            type: "string",
            defaultValue: "",
        },
        picture: {
            name: "Picture",
            type: "string",
            defaultValue: "",
        },
        locale: {
            name: "Locale",
            type: "string",
            defaultValue: "en",
        },
        company: {
            name: "Company",
            type: "string",
            defaultValue: ""
        },
        globalPermissions: {
            name: "Global Permissions",
            type: "object",
            defaultValue: [],
            hideInApi: true,
        },
        featureFlags: {
            name: "Feature Flags",
            type: "object",
            defaultValue: [],
            userEditable: false,
            hideInApi: true,
        },
        notifyErrors: {
            name: "Notify Errors",
            type: "boolean",
            defaultValue: true,
        },
        notifyEmail: {
            name: "Notification Email",
            description: "Email where to send notifications of integrations issues. If not filled, it will be send to default email.",
            type: "string",
            defaultValue: "",
            trim: true,
            filterable: true,
            userEditable: true,
            validations: {
                isEmail: true
            },
            visibility: (data) => data.notifyErrors,
        },
        notifyErrorsFreq: {
            name: "Notify Frequency",
            type: "enum",
            defaultValue: "daily",
            values: NOTIFY_FREQ,
            visibility: (data) => data.notifyErrors,
        },
        channelErrorTypes: {
            name: "Channel Error Types",
            type: "object",
            defaultValue: [],
            args: {
                author_id: {
                    type: "string"
                },
                displayNameLike: {
                    type: "string"
                },
                resolvableBy: {
                    type: "string"
                },
                type: {
                    type: "string"
                },
                hasError: {
                    type: "boolean"
                },
                flowId: {
                    type: "string"
                },
                organisationId: {
                    type: "string"
                },
                lastErrorTransferAtAfter: {
                    type: "datetime"
                },
                lastErrorTransferAtBefore: {
                    type: "datetime"
                },
            },
            controllerFn: 'getChannelErrorsGroupBy',
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        instance: {
            name: "Instance",
            type: "relation",
            to: () => types['instances'],
            userEditable: false,
            hiddenCreate: true,
            defaultValue: (parent) => {
                if (parent) {
                    return parent['instance_id'];
                }
            },
        },
		// saved value in DB but returns all IoT config for current user
		iotTopic: {
			name: "IoT communicable properties",
			type: "string",
            userEditable: false,
            import: false,
            export: false,
            isPublic: true,
			controllerFn: "getIotConfig"
		},
    }; },
    relations: (types) => { return {
        validAnnouncements: {
            name: "Instance",
            type: 'manyToOne',
            to: types['announcements'],
            shouldFollow: false,
			controllerFn: "getValidAnnouncements",
        },
        organisations2user: {
            type: 'manyToOne',
            to: types['organisation2user'],
            shouldFollow: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "user_id"
                }
            ]
        },
        developerGroups2user: {
            type: 'manyToOne',
            to: types['developerGroup2users'],
            shouldFollow: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "user_id"
                }
            ]
        },
        developerGroups: {
            type: 'manyToOne',
            to: types['developerGroups'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAccessibleDeveloperGroups',
        },
        channelErrors: {
            type: 'oneToMany',
            to: types['channelErrors'],
            args: {
                consultant: {
                    type: "boolean"
                },
                withoutConsultant: {
                    type: "boolean"
                },
                forConsultant: {
                    type: "string"
                },
                forAll: {
                    type: "boolean"
                },
                referencedId: {
                    type: "string"
                },
                referencedIdLike: {
                    type: "string"
                },
                displayNameLike: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getChannelErrors',
        },
        notifications: {
            type: 'oneToMany',
            to: types['organisationNotifications'],
            args: {
                consultant: {
                    type: "boolean"
                },
            },
            shouldFollow: false,
            controllerFn: 'getOrganisationNotifications',
        },
        identities: {
            type: 'manyToOne',
            to: types['userIdentity'],
            shouldFollow: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "user_id"
                }
            ]
        },
        apiKeys: {
            type: 'manyToOne',
            to: types['apiKeys'],
            shouldFollow: false,
            foreignKeys: [
                {
                    from: "id",
                    to: "user_id"
                }
            ]
        },
        organisations: {
            type: 'oneToMany',
            to: types['organisations'],
            args: {
                id: {
                    type: "string"
                },
                externalId: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAccessibleOrganisations',
        },
        accessibleOrganisations: {
            type: 'oneToMany',
            to: types['organisations'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAccessibleOrganisations',
        },
        accessibleUsers: {
            type: 'oneToMany',
            to: types['users'],
            args: {
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getAccessibleUsers',
        },
        accessibleOrganisation2users: {
            type: 'oneToMany',
            to: types['organisation2user'],
            shouldFollow: false,
            controllerFn: 'getAccessibleOrganisation2User',
        },
        invitationToAccept: {
            type: 'oneToMany',
            hasPaging: false,
            to: types['invitations'],
            args: {
                secret: {
                    type: "string"
                },
                id: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getInvitationByCode',
        },
        channelDefs: {
            type: 'manyToOne',
            to: types['channelDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        myInstances: {
            type: 'manyToOne',
            to: types['instances'],
        },
        publicChannelDefs: {
            type: 'manyToOne',
            to: types['channelDefs'],
            isPublic: true,
            controllerFn: 'getPublicChannelDefs',
        },
        flowDefs: {
            type: 'manyToOne',
            to: types['flowDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        flowDefVersions: {
            type: 'manyToOne',
            to: types['flowDefVersions'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        channelDefVersions: {
            type: 'manyToOne',
            to: types['channelDefVersions'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        libraryDefs: {
            type: 'manyToOne',
            to: types['libraryDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        libraryDefVersions: {
            type: 'manyToOne',
            to: types['libraryDefVersions'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        addonDefs: {
            type: 'manyToOne',
            to: types['addonDefs'],
            isPublic: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        profileDefs: {
            type: 'manyToOne',
            to: types['profileDefs'],
            isPublic: true,
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        scenarios: {
            type: 'manyToOne',
            to: types['scenarios'],
            isPublic: true,
        },
        publicScenarios: {
            type: 'manyToOne',
            to: types['scenarios'],
            isPublic: true,
            controllerFn: 'getPublicScenarios',
        },
        scenarioDefs: {
            type: 'manyToOne',
            to: types['scenarioDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        publicScenarioDefs: {
            type: 'manyToOne',
            to: types['scenarioDefs'],
            isPublic: true,
            controllerFn: 'getPublicScenarioDefs',
        },
        channelDefsToAdd: {
            type: 'manyToOne',
            to: types['channelDefs'],
            controllerFn: 'getChannelDefsToAdd',
        },
        flowDefsToAdd: {
            type: 'manyToOne',
            to: types['flowDefs'],
            controllerFn: 'getFlowDefsToAdd',
        },
        addonDefsToAdd: {
            type: 'manyToOne',
            to: types['addonDefs'],
            controllerFn: 'getAddonDefsToAdd',
        },
        entityViewDefs: {
            type: 'manyToOne',
            to: types['entityViewDefs'],
            isPublic: true,
        },
        groupDefs: {
            type: 'manyToOne',
            to: types['groupDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        coordinationDefs: {
            type: 'manyToOne',
            to: types['coordinationDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        entityDefs: {
            type: 'manyToOne',
            to: types['entityDefs'],
            permission: Permission.PERM_EDIT_CHANNEL_DEF,
        },
        jobs: {
            type: 'oneToMany',
            to: types['jobs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "author_id",
                    asFilterOnly: true,
                }
            ]
        },
        relevantScenarios: {
            type: 'oneToMany',
            to: types['scenarios'],
            isPublic: true,
            args: {
                searchName: {
                    type: 'string',
                },
                first: {
                    type: 'int'
                },
                requiredPayPlan: {
                    type: 'string'
                },
                nameLike: {
                    type: 'string'
                },
				channelDefIds: {
					type: 'stringArray'
				},
				channelDefUrlFragments: {
					type: 'stringArray'
				},
				channelDefUrlFragmentsJoinType: {
					type: 'string',
					defaultValue: 'and'
				}
            },
            controllerFn: 'getRelevantScenarios',
        },

    }; }
});
